import {
  MONTH_DAY_SHORT_FORMAT,
  getDate,
  getDateInFormat,
  getTimestampInMillisecondsFromSecond
} from "@/helpers/date.helpers"
import { DISCOVERED_CLOSED_ISSUES_MAX } from "@/pages/DashboardPage/const/graph"

import type { DiscoveredClosedIssuesResponse } from "@/api/useStatistics.types"
import type { DateDisplayFormat } from "@/helpers/date.helpers.types"

export const discoveredClosedIssuesSelector = (
  response: DiscoveredClosedIssuesResponse
) => {
  const { buckets = [], aggregatedData, ...restResponse } = response?.data || {}
  const period =
    (aggregatedData?.bucketGranularity?.slice(0, -1) as
      | "day"
      | "week"
      | "month") || "day"

  const EXPONENT = 0.4
  const transformedExponent = (value: number) =>
    Math.sign(value) * Math.pow(Math.abs(value), EXPONENT)

  const allValues = buckets.flatMap((item) => [
    item.openedCount,
    item.resolvedCount
  ])
  const dataMax = allValues.length > 0 ? Math.max(...allValues) : 0

  const getLocalizedDateFormat = (
    timestamp: number,
    format: DateDisplayFormat = MONTH_DAY_SHORT_FORMAT
  ) =>
    getDateInFormat(
      getDate(getTimestampInMillisecondsFromSecond(timestamp)).local(),
      format
    )

  const getLabel = (
    record: DiscoveredClosedIssuesResponse["data"]["buckets"][number],
    currentPeriod = period
  ) => {
    const { startTimestamp, endTimestamp } = record

    if (currentPeriod === "day") return getLocalizedDateFormat(startTimestamp)
    if (currentPeriod === "month")
      return getLocalizedDateFormat(startTimestamp, "MMM")

    return `${getLocalizedDateFormat(startTimestamp)} - ${getLocalizedDateFormat(
      endTimestamp
    )}`
  }

  const defaultMax = DISCOVERED_CLOSED_ISSUES_MAX - 100
  const exponentialMax = transformedExponent(dataMax)
  const normalize = (v: number) => {
    if (dataMax === 0 || exponentialMax === 0) return 0

    return (transformedExponent(v) / exponentialMax) * defaultMax
  }

  return {
    buckets: buckets
      .reduce(
        (acc, record) => {
          const { resolvedCount, openedCount } = record
          const label = getLabel(record, period)

          acc.push({
            label,
            type: "discovered",
            value: normalize(openedCount),
            ...record
          })

          acc.push({
            label,
            type: "resolved",
            value: 0 - normalize(resolvedCount),
            ...record
          })

          return acc
        },
        [] as {
          type: "discovered" | "resolved" | "dismissed"
          value: number
          label: string
          startTimestamp: number
          endTimestamp: number
        }[]
      )
      .sort((a, b) => {
        if (a.label !== b.label) {
          return a.startTimestamp - b.startTimestamp
        }

        const orderMap = {
          dismissed: 1,
          resolved: 2,
          discovered: 3
        }

        return (
          orderMap[a.type as keyof typeof orderMap] -
          orderMap[b.type as keyof typeof orderMap]
        )
      }),
    aggregatedData: {
      period,
      ...aggregatedData
    },
    ...restResponse
  }
}
